import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "@/lib/trpc";
import {
  cancelClarityOneMeQueries,
  planClarityOneMeRetry,
} from "@/lib/clarityOneMeQuery";
import {
  CLARITY_ONE_SESSION_TIMEOUT_MS,
  presentClarityOneSession,
  type ClarityOneSessionPresentation,
} from "@/lib/clarityOneSessionGate";

export function useClarityOneMeSession() {
  const queryClient = useQueryClient();
  const [timedOut, setTimedOut] = useState(false);
  const [paused, setPaused] = useState(false);

  const meQuery = trpc.clarityOneAuth.me.useQuery(undefined, {
    enabled: !paused,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const sessionView: ClarityOneSessionPresentation = presentClarityOneSession({
    isLoading: meQuery.isLoading,
    isFetching: meQuery.isFetching,
    isError: meQuery.isError,
    isSuccess: meQuery.isSuccess,
    data: meQuery.data ?? null,
    timedOut,
  });

  useEffect(() => {
    if (sessionView.kind !== "loading") return;
    const timer = window.setTimeout(() => {
      setTimedOut(true);
      setPaused(true);
      void cancelClarityOneMeQueries(queryClient);
    }, CLARITY_ONE_SESSION_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [sessionView.kind, queryClient]);

  useEffect(() => {
    if (sessionView.kind === "ready") {
      setTimedOut(false);
      setPaused(false);
    }
  }, [sessionView.kind]);

  const retry = () => {
    const plan = planClarityOneMeRetry(paused);
    setTimedOut(false);
    if (plan === "unpause") {
      setPaused(false);
      return;
    }
    void meQuery.refetch();
  };

  return { meQuery, sessionView, retry, paused };
}
